// Per-row props for MessageRow that keep their identity while their contents
// do not change (#1346 item 3, "page unresponsive").
//
// MessageRow is React.memo'd, so a row re-renders only when a prop changes
// identity. AgentChat used to hand every row values rebuilt on every change
// anywhere in the conversation: the artifact lists (rebuilt per streamed
// delta), the whole plugin fold (re-folded from scratch per plugin event), the
// tool-call maps, and isStreaming. Measured on a real long session's heaviest
// turn, 227 of 272 row renders were such wasted renders, 150 of them on rows
// of history. Each one re-draws that row's Carbon tables and charts.
//
// Two tools here:
//   - PluginFold: folds plugin events INCREMENTALLY, so a tool call's plugin
//     state is a new object only when an event for that tool call arrives;
//   - RowPropsCache: builds each row's slice of the maps (only its own tool
//     calls) and reuses last render's object when the contents are the same.

import type { ArtifactInfo, AgentMessage, AskUserQuestionInfo, CreatedDashboardInfo, RenderedChartInfo, RenderedTableInfo, AgentSSEEvent as CoreSSEEvent } from '../types.js'
import type { AgentSSEEvent, RenderPlugin } from '../plugins.js'

export type PluginStates = Map<number, Map<string, unknown>>

function pluginEventKey(ev: CoreSSEEvent): string {
  const d = ev.data as Record<string, unknown> | undefined
  return (d?.toolCallId as string | undefined)
    || (d?.tool_use_id as string | undefined)
    || (d?.messageId as string | undefined)
    || ''
}

/**
 * Incremental version of foldPluginEvents. When the new events array extends
 * the one folded last time (the live case: one event appended), only the new
 * events are folded, into copies of just the maps they touch; everything else
 * keeps its identity. Any other change (a restore, a rewind) folds from
 * scratch. Same result as foldPluginEvents either way.
 */
export class PluginFold {
  private plugins: RenderPlugin[] | null = null
  private events: CoreSSEEvent[] = []
  private states: PluginStates = new Map()

  fold(plugins: RenderPlugin[], events: CoreSSEEvent[]): PluginStates {
    // Element-wise, so a host passing a fresh array of the same plugins each
    // render still folds incrementally.
    const samePlugins = this.plugins !== null
      && plugins.length === this.plugins.length
      && plugins.every((p, i) => p === this.plugins![i])
    if (samePlugins && events === this.events) return this.states
    const n = this.events.length
    const extends_ = samePlugins
      && events.length >= n
      && (n === 0 || events[n - 1] === this.events[n - 1])
    const fresh = !extends_
    const states: PluginStates = fresh ? new Map() : new Map(this.states)
    if (fresh) for (let i = 0; i < plugins.length; i++) states.set(i, new Map())
    const copied = new Set<number>()
    const typeSets = plugins.map(p => new Set(p.eventTypes))
    for (let e = fresh ? 0 : n; e < events.length; e++) {
      const ev = events[e]
      for (let i = 0; i < plugins.length; i++) {
        if (!typeSets[i].has(ev.type)) continue
        let byTool = states.get(i)!
        if (!fresh && !copied.has(i)) { byTool = new Map(byTool); states.set(i, byTool); copied.add(i) }
        const key = pluginEventKey(ev)
        const pluginEv: AgentSSEEvent = { type: ev.type, data: ev.data as Record<string, unknown>, timestamp: ev.timestamp }
        const prev = byTool.has(key) ? byTool.get(key) : plugins[i].init()
        byTool.set(key, plugins[i].reduce(prev, pluginEv))
      }
    }
    this.plugins = plugins
    this.events = events
    this.states = states
    return states
  }
}

const EMPTY_PLUGIN_STATES: PluginStates = new Map()
const EMPTY_TABLES = new Map<string, RenderedTableInfo[]>()
const EMPTY_CHARTS = new Map<string, RenderedChartInfo[]>()
const EMPTY_QUESTIONS = new Map<string, AskUserQuestionInfo>()
const EMPTY_DASHBOARDS = new Map<string, CreatedDashboardInfo>()

export interface RowSlice {
  autoArtifacts?: ArtifactInfo[]
  toolArtifacts?: ArtifactInfo[]
  byPlugin: PluginStates
  toolCallTables: Map<string, RenderedTableInfo[]>
  toolCallCharts: Map<string, RenderedChartInfo[]>
  toolCallQuestions: Map<string, AskUserQuestionInfo>
  toolCallDashboards: Map<string, CreatedDashboardInfo>
  /** isStreaming, but only for a row that uses it (it disables its question cards). */
  isStreaming: boolean
}

export interface ConversationMaps {
  autoArtifacts: Map<string, ArtifactInfo[]>
  toolArtifacts: Map<string, ArtifactInfo[]>
  byPlugin: PluginStates
  toolCallTables: Map<string, RenderedTableInfo[]>
  toolCallCharts: Map<string, RenderedChartInfo[]>
  toolCallQuestions: Map<string, AskUserQuestionInfo>
  toolCallDashboards: Map<string, CreatedDashboardInfo>
  isStreaming: boolean
}

function shallowEqualObject(a: object, b: object): boolean {
  if (a === b) return true
  const ka = Object.keys(a)
  if (ka.length !== Object.keys(b).length) return false
  for (const k of ka) if ((a as Record<string, unknown>)[k] !== (b as Record<string, unknown>)[k]) return false
  return true
}

/** Artifact lists are rebuilt (and their objects re-created by DB loads) freely; compare by content. */
function sameArtifacts(a: ArtifactInfo[] | undefined, b: ArtifactInfo[] | undefined): boolean {
  if (a === b) return true
  if (!a || !b || a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (!shallowEqualObject(a[i], b[i])) return false
  return true
}

function sameArray<T>(a: T[], b: T[]): boolean {
  if (a === b) return true
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}

function sameMap<V>(a: Map<string, V>, b: Map<string, V>, eq: (x: V, y: V) => boolean): boolean {
  if (a === b) return true
  if (a.size !== b.size) return false
  for (const [k, v] of a) {
    if (!b.has(k)) return false
    if (!eq(v, b.get(k)!)) return false
  }
  return true
}

const same = <V>(x: V, y: V) => x === y

function samePluginStates(a: PluginStates, b: PluginStates): boolean {
  if (a === b) return true
  if (a.size !== b.size) return false
  for (const [i, m] of a) {
    const n = b.get(i)
    if (!n || !sameMap(m, n, same)) return false
  }
  return true
}

/** Keep `prev` when `next` has the same contents, so the memoised row sees no change. */
function keep<T>(prev: T | undefined, next: T, eq: (a: T, b: T) => boolean): T {
  return prev !== undefined && eq(prev, next) ? prev : next
}

export class RowPropsCache {
  private rows = new Map<string, RowSlice>()

  /** One slice per message, reusing last render's pieces wherever unchanged. */
  build(messages: AgentMessage[], maps: ConversationMaps): RowSlice[] {
    const next = new Map<string, RowSlice>()
    const out = messages.map(message => {
      const prev = this.rows.get(message.id)
      const ids = message.toolCalls?.map(tc => tc.id) ?? []

      let byPlugin = EMPTY_PLUGIN_STATES
      let tables = EMPTY_TABLES
      let charts = EMPTY_CHARTS
      let questions = EMPTY_QUESTIONS
      let dashboards = EMPTY_DASHBOARDS
      if (ids.length > 0) {
        for (const [i, byTool] of maps.byPlugin) {
          for (const id of ids) {
            if (!byTool.has(id)) continue
            if (byPlugin === EMPTY_PLUGIN_STATES) byPlugin = new Map()
            let m = byPlugin.get(i)
            if (!m) { m = new Map(); byPlugin.set(i, m) }
            m.set(id, byTool.get(id))
          }
        }
        for (const id of ids) {
          const t = maps.toolCallTables.get(id)
          if (t) { if (tables === EMPTY_TABLES) tables = new Map(); tables.set(id, t) }
          const c = maps.toolCallCharts.get(id)
          if (c) { if (charts === EMPTY_CHARTS) charts = new Map(); charts.set(id, c) }
          const q = maps.toolCallQuestions.get(id)
          if (q) { if (questions === EMPTY_QUESTIONS) questions = new Map(); questions.set(id, q) }
          const d = maps.toolCallDashboards.get(id)
          if (d) { if (dashboards === EMPTY_DASHBOARDS) dashboards = new Map(); dashboards.set(id, d) }
        }
      }

      const slice: RowSlice = {
        autoArtifacts: keep(prev?.autoArtifacts, maps.autoArtifacts.get(message.id), sameArtifacts),
        toolArtifacts: keep(prev?.toolArtifacts, maps.toolArtifacts.get(message.id), sameArtifacts),
        byPlugin: keep(prev?.byPlugin, byPlugin, samePluginStates),
        toolCallTables: keep(prev?.toolCallTables, tables, (a, b) => sameMap(a, b, sameArray)),
        toolCallCharts: keep(prev?.toolCallCharts, charts, (a, b) => sameMap(a, b, sameArray)),
        toolCallQuestions: keep(prev?.toolCallQuestions, questions, (a, b) => sameMap(a, b, same)),
        toolCallDashboards: keep(prev?.toolCallDashboards, dashboards, (a, b) => sameMap(a, b, same)),
        isStreaming: maps.isStreaming && questions.size > 0,
      }
      next.set(message.id, slice)
      return slice
    })
    this.rows = next
    return out
  }
}
